// src/components/CreditCardReconciliation.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { TailSpin } from "react-loader-spinner";
import { toast } from "react-toastify";
import TagSelector from ".//TagSelector";

import "../styles/CreditCardReconciliation.css";

const CreditCardReconciliation = ({
  onClose,
  onComplete,
  homeCurrency = "USD",
}) => {
  // Step management
  const [currentStep, setCurrentStep] = useState(1); // 1 = Upload, 2 = Review
  const [exchangeRateUpdates, setExchangeRateUpdates] = useState([]);
  const [activeReviewTab, setActiveReviewTab] = useState("missing");
  const [nrLoadingIds, setNrLoadingIds] = useState([]);
  const [isUpdatingAllRates, setIsUpdatingAllRates] = useState(false);
  const [updatingRateIds, setUpdatingRateIds] = useState([]);

  const [isCompleting, setIsCompleting] = useState(false);

  const [originalProcessingId, setOriginalProcessingId] = useState(null);

  const [isCleaningUp, setIsCleaningUp] = useState(false);

  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);

  const [processedTransactions, setProcessedTransactions] = useState({});
  const [ignoredTransactions, setIgnoredTransactions] = useState([]);

  // File upload state
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Clean up jobs
  const [processingIds, setProcessingIds] = useState([]);

  // Receipt upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedTag, setSelectedTag] = useState("");
  const fileInputRef = React.useRef(null);

  // Data state
  const [ccTransactions, setCcTransactions] = useState([]);

  // We keep these as they're used in data processing, but mark as used
  // eslint-disable-next-line no-unused-vars
  const [expenses, setExpenses] = useState([]);
  const [matches, setMatches] = useState([]);
  const [missingReceipts, setMissingReceipts] = useState([]);

  // Future feature - keeping it disabled
  // eslint-disable-next-line no-unused-vars
  const [mismatchedAmounts, setMismatchedAmounts] = useState([]);

  const rememberProcessingId = (id) => {
    if (id) {
      setProcessingIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }
  };

  const cleanupAllJobs = useCallback(async () => {
    // Prevent duplicate cleanup calls
    if (isCleaningUp) {
      console.log("Cleanup already in progress, skipping duplicate call");
      return;
    }

    // Create a set of all IDs to clean up, including originalProcessingId if it exists
    const idsToCleanup = [...processingIds];

    // Add originalProcessingId if it exists and isn't already included
    if (originalProcessingId && !idsToCleanup.includes(originalProcessingId)) {
      idsToCleanup.push(originalProcessingId);
    }

    if (idsToCleanup.length === 0) return;

    try {
      setIsCleaningUp(true);
      console.log(
        `Cleaning up ${idsToCleanup.length} processing jobs:`,
        idsToCleanup
      );

      await Promise.all(
        idsToCleanup.map((id) =>
          axios
            .post(
              "/api/complete-reconciliation",
              { processingId: id },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              }
            )
            .catch((err) => console.error(`Error cleaning up job ${id}:`, err))
        )
      );

      setProcessingIds([]);
      setOriginalProcessingId(null);
    } catch (error) {
      console.error("Error during job cleanup:", error);
    } finally {
      setIsCleaningUp(false);
    }
  }, [processingIds, originalProcessingId, isCleaningUp]);

  // Add this effect for cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAllJobs();
    };
  }, [cleanupAllJobs]);

  const handleIgnoreTransaction = (transaction) => {
    // Add to ignored list
    setIgnoredTransactions((prev) => [...prev, transaction.uid]);

    // Remove from missing receipts list
    setMissingReceipts((prev) => prev.filter((t) => t.uid !== transaction.uid));

    // Toast removed
  };

  // Update this function in CreditCardReconciliation.js
  // Find this function in CreditCardReconciliation.js
  const handleCsvUpload = async () => {
    if (!file) {
      setUploadError("Please select a file first.");
      return;
    }

    try {
      setIsUploading(true);
      setUploadError(null);

      // Show progress indicator for synchronous processing too
      setBatchProcessing(true);
      setBatchProgress(15); // Start with some initial progress

      // Determine if this is a PDF file
      const isPdf = file.name.toLowerCase().endsWith(".pdf");
      const isImage = /\.(jpe?g|png|gif)$/i.test(file.name);

      if (isPdf || isImage) {
        // Step 1: Get a signed URL from your server
        try {
          const fileType = isPdf ? "pdf" : "image";
          const contentType = isPdf
            ? "application/pdf"
            : file.type || "image/jpeg";

          const urlResponse = await axios.post(
            "/api/get-upload-url",
            {
              filename: file.name,
              contentType: contentType,
              fileType: fileType,
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          if (!urlResponse.data.success) {
            throw new Error(
              urlResponse.data.message || "Failed to get upload URL"
            );
          }

          const { signedUrl, gcsFileName } = urlResponse.data;

          // Step 2: Upload the file directly to GCS using the signed URL
          setBatchProgress(30); // Update progress
          await axios.put(signedUrl, file, {
            headers: {
              "Content-Type": contentType,
            },
            onUploadProgress: (progressEvent) => {
              // Calculate upload progress (30-70% of total)
              const uploadProgress =
                30 +
                Math.round((progressEvent.loaded / progressEvent.total) * 40);
              setBatchProgress(uploadProgress);
            },
          });

          // Step 3: Tell your server to process the uploaded file
          setBatchProgress(75); // File uploaded, now processing
          const endpoint = isPdf
            ? "/api/process-credit-card-pdf-from-gcs"
            : "/api/process-credit-card-image-from-gcs";

          // REPLACE WITH:
          const response = await axios.post(
            endpoint,
            { gcsFileName },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          // Set progress to 100% when we get the response
          setBatchProgress(100);

          if (response.data.success) {
            if (response.data.processing) {
              // Handle async processing for image uploads
              const processingId = response.data.processingId;
              startPolling(processingId);
            } else {
              // Handle synchronous response
              // Small delay to show 100% before completing
              setTimeout(() => {
                handleProcessingComplete(response.data);
                setBatchProcessing(false);
                setIsUploading(false);
              }, 500);
            }
          } else {
            setUploadError(
              response.data.message ||
                `Failed to process ${isPdf ? "PDF" : "image"} file.`
            );
            setBatchProcessing(false);
            setIsUploading(false);
          }
        } catch (error) {
          console.error(
            `Error processing ${isPdf ? "PDF" : "image"} file:`,
            error
          );
          setUploadError(
            error.response?.data?.message ||
              `An error occurred while processing the ${
                isPdf ? "PDF" : "image"
              } file.`
          );
          setBatchProcessing(false);
          setIsUploading(false);
        }
      } else {
        // For non-PDF files, continue using the existing approach
        const formData = new FormData();
        formData.append("csvFile", file);

        // Log what we're sending
        console.log("Uploading file:", {
          name: file.name,
          size: file.size,
          type: file.type,
          formField: "csvFile",
        });

        // Simulate progress while processing synchronously
        const progressInterval = setInterval(() => {
          setBatchProgress((prev) => {
            // Don't go past 80% until we get the response
            return prev < 80 ? prev + 5 : prev;
          });
        }, 500);

        // Ensure proper headers for multipart/form-data
        const response = await axios.post(
          "/api/process-credit-card",
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        // Clear the progress interval
        clearInterval(progressInterval);

        // Set progress to 100% when we get the response
        setBatchProgress(100);

        if (response.data.success) {
          if (response.data.processing) {
            // Handle async processing case (already has its own progress handling)
            const processingId = response.data.processingId;
            startPolling(processingId);
            toast.info(
              "Your statement is being processed. This may take a moment."
            );
          } else {
            // Handle synchronous response
            // Small delay to show 100% before completing
            setTimeout(() => {
              handleProcessingComplete(response.data);
              setBatchProcessing(false);
              setIsUploading(false);
            }, 500);
          }
        } else {
          setUploadError(
            response.data.message || "Failed to process CSV file."
          );
          setBatchProcessing(false);
          setIsUploading(false);
        }
      }
    } catch (error) {
      console.error(
        `Error processing ${file.name.endsWith(".pdf") ? "PDF" : "CSV"} file:`,
        error
      );
      setUploadError(
        error.response?.data?.message ||
          "An error occurred while processing the file."
      );
      setBatchProcessing(false);
      setIsUploading(false);
    }
  };
  // Start polling for results with progress
  const startPolling = (processingId) => {
    setOriginalProcessingId(processingId);
    rememberProcessingId(processingId);

    const pollInterval = 2000; // 2 seconds
    let attempts = 0;
    const maxAttempts = 60; // About 2 minutes of polling

    // Add an overall timeout to guarantee escape from loading state
    const overallTimeout = setTimeout(() => {
      console.warn("Overall polling timeout reached");
      setUploadError("Processing took too long - please try again later.");
      setBatchProcessing(false);
      setIsUploading(false);
    }, 5 * 60 * 1000); // 5 minutes absolute max

    // Set initial progress state
    setBatchProcessing(true);
    setBatchProgress(5); // Start with 5% to show activity

    const pollForResults = async () => {
      try {
        attempts++;
        console.log(
          `Polling attempt ${attempts} for processingId: ${processingId}`
        );

        // First check processing status
        const statusResponse = await axios.get(
          `/api/credit-card-processing-status/${processingId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        console.log(`Status response:`, statusResponse.data);

        // Check if processing is completed or failed
        if (statusResponse.data.status === "completed") {
          // Clear the overall timeout
          clearTimeout(overallTimeout);

          // Processing completed
          setBatchProgress(100);

          // Short delay to show 100% before moving to results
          setTimeout(() => {
            handleProcessingComplete(statusResponse.data.result);
            setBatchProcessing(false);
            setIsUploading(false);
          }, 500);

          return;
        } else if (statusResponse.data.status === "failed") {
          // Clear the overall timeout
          clearTimeout(overallTimeout);

          // Processing failed
          setUploadError(
            statusResponse.data.message || "Failed to process your statement."
          );
          setBatchProcessing(false);
          setIsUploading(false);
          return;
        }

        // Now get detailed progress
        const progressResponse = await axios.get(
          `/api/credit-card-processing-progress/${processingId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        console.log(`Progress response:`, progressResponse.data);

        if (progressResponse.data.success) {
          // Update progress percentage
          const progress =
            progressResponse.data.progress ||
            (progressResponse.data.totalItems > 0
              ? Math.round(
                  (progressResponse.data.processedItems /
                    progressResponse.data.totalItems) *
                    100
                )
              : Math.min(5 + attempts * 5, 90)); // Fallback to incremental progress

          setBatchProgress(progress);

          // Update message if available
          if (progressResponse.data.message) {
            // Optionally show message to user
            console.log(`Progress message: ${progressResponse.data.message}`);
          }
        }

        // Continue polling if still pending and not reached max attempts
        if (attempts < maxAttempts) {
          setTimeout(pollForResults, pollInterval);
        } else {
          // Clear the overall timeout
          clearTimeout(overallTimeout);

          // Timed out
          setUploadError(
            "Processing is taking longer than expected. Please check back later."
          );
          setBatchProcessing(false);
          setIsUploading(false);
        }
      } catch (error) {
        console.error("Error polling for results:", error);

        // Add more detailed error logging
        console.error("Status code:", error?.response?.status);
        console.error("Error message:", error?.message);

        // Show specific error for 404 (missing endpoint)
        if (error?.response?.status === 404) {
          setUploadError(
            "Processing status endpoint not available. Please check server configuration."
          );
        } else {
          setUploadError("Error checking processing status. Please try again.");
        }

        // Clear the overall timeout
        clearTimeout(overallTimeout);

        // Always reset UI state on error
        setBatchProcessing(false);
        setIsUploading(false);
      }
    };

    // Start polling (with a small initial delay)
    setTimeout(pollForResults, 1000);
  };

  // Handle completed processing
  const handleProcessingComplete = (data) => {
    // Add unique IDs to all transactions and lists
    const addUid = (arr) =>
      arr.map((item, idx) => ({ ...item, uid: `${Date.now()}-${idx}` }));

    // Set all the returned data WITH unique IDs
    // Set all the returned data WITH unique IDs
    setCcTransactions(addUid(data.transactions));
    setExpenses(data.expenses); // This one doesn't need UIDs
    // Filter out any matches without a transaction object
    const normalisedMatches = data.matches.filter((m) => m.transaction);
    setMatches(addUid(normalisedMatches));

    // Add selected property to each transaction for batch operations
    setMissingReceipts(
      addUid(data.missingReceipts)
        .filter((t) => {
          const dateObj = new Date(t.date);
          return !isNaN(dateObj.getTime()); // Filter out invalid dates
        })
        .map((t) => ({
          ...t,
          selected: false,
        }))
    );

    setExchangeRateUpdates(data.exchangeRateUpdates);
    setMismatchedAmounts(data.mismatchedAmounts || []);

    // Move to review step
    setCurrentStep(2);
    setIsUploading(false);
  };

  // Function to update all exchange rates at once
  // Function to update all exchange rates at once
  const handleUpdateAllExchangeRates = async (updatesToProcess = null) => {
    try {
      setIsUpdatingAllRates(true);

      // Use provided updates or filter the exchange rate updates
      const updates =
        updatesToProcess ||
        exchangeRateUpdates.map((update) => {
          // Ensure functional amount is valid
          const functionalAmount =
            update.newFunctionalAmount && update.newFunctionalAmount > 0
              ? update.newFunctionalAmount
              : update.match.transaction.amount / update.newRate;

          return {
            rowIndex: update.match.receipt.rowIndex,
            exchangeRate: update.newRate,
            functionalAmount: functionalAmount,
          };
        });

      // Filter to only include foreign transactions
      const validUpdates = updates.filter((update) => {
        // If we have custom update objects, just use them
        if (updatesToProcess) return true;

        // Otherwise check if it's a foreign transaction
        const match = exchangeRateUpdates.find(
          (item) => item.match.receipt.rowIndex === update.rowIndex
        );
        if (!match) return false;

        const transaction = match.match?.transaction;
        const receipt = match.match?.receipt;

        // Only include if either the transaction or receipt is in a foreign currency
        return (
          transaction &&
          (transaction.currency !== homeCurrency ||
            receipt.currency !== homeCurrency) &&
          Math.abs(match.oldRate - match.newRate) > 0.0001
        );
      });

      if (validUpdates.length === 0) {
        toast.info("No valid exchange rates to update");
        setIsUpdatingAllRates(false);
        return;
      }

      const response = await axios.post(
        "/api/update-all-exchange-rates",
        {
          updates: validUpdates,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(
          `Updated ${validUpdates.length} exchange rates successfully!`
        );

        // Remove the processed updates from the state
        if (updatesToProcess) {
          const processedRowIndexes = validUpdates.map((u) => u.rowIndex);
          setExchangeRateUpdates((prev) =>
            prev.filter(
              (update) =>
                !processedRowIndexes.includes(update.match.receipt.rowIndex)
            )
          );
        } else {
          setExchangeRateUpdates([]);
        }
      } else {
        toast.error(
          response.data.message || "Failed to update exchange rates."
        );
      }
    } catch (error) {
      console.error("Error updating exchange rates:", error);
      toast.error(
        error.response?.data?.message ||
          "An error occurred while updating exchange rates."
      );
    } finally {
      setIsUpdatingAllRates(false);
    }
  };

  // Function to update exchange rate for a matched transaction/receipt pair
  // Function to update exchange rate for a matched transaction/receipt pair
  const handleUpdateExchangeRate = async (data) => {
    try {
      // Make sure we have the rowIndex
      if (!data.rowIndex) {
        toast.error("Missing row index for update");
        return;
      }

      // Add this row to the updating list
      setUpdatingRateIds((prev) => [...prev, data.rowIndex]);

      // Use the existing functional amount from the transaction (from credit card statement)
      const functionalAmount = data.functionalAmount;

      // Call the API to update the exchange rate (column V)
      const response = await axios.post(
        "/api/update-exchange-rate",
        {
          rowIndex: data.rowIndex, // Only send the rowIndex
          exchangeRate: data.exchangeRate, // And the exchange rate
          functionalAmount: functionalAmount, // And the functional amount if available
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        // Update the receipt object in our local state
        setMatches((prev) =>
          prev.map((match) => {
            if (match.receipt.rowIndex === data.rowIndex) {
              return {
                ...match,
                receipt: {
                  ...match.receipt,
                  exchangeRate: data.exchangeRate,
                  functionalTotal: functionalAmount,
                },
              };
            }
            return match;
          })
        );

        // Remove the updated item from exchangeRateUpdates
        setExchangeRateUpdates((prev) =>
          prev.filter(
            (update) => update.match.receipt.rowIndex !== data.rowIndex
          )
        );

        toast.success(
          "Exchange rate and functional totals updated successfully"
        );
      } else {
        toast.error(response.data.message || "Failed to update exchange rate");
      }
    } catch (error) {
      console.error("Error updating exchange rate:", error);
      toast.error(
        error.response?.data?.message ||
          "An error occurred while updating the exchange rate and functional totals"
      );
    } finally {
      // Remove this row from the updating list
      setUpdatingRateIds((prev) => prev.filter((id) => id !== data.rowIndex));
    }
  };

  // Function to mark a transaction as "No Receipt Required"
  const handleMarkNoReceiptRequired = async (transaction) => {
    setNrLoadingIds((prev) => [...prev, transaction.uid]);
    try {
      const response = await axios.post(
        "/api/mark-no-receipt-required",
        transaction, // Send the entire transaction object
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        // Update the processedTransactions state
        setProcessedTransactions((prev) => ({
          ...prev,
          [transaction.uid]: {
            status: "noReceiptRequired",
            receipt: response.data.receipt,
          },
        }));

        // Remove from missing receipts list
        // Remove from missing receipts list
        setMissingReceipts((prev) =>
          prev.filter((t) => t.uid !== transaction.uid)
        );
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Error marking transaction:", error);
      toast.error(
        error.response?.data?.message ||
          "An error occurred while marking the transaction."
      );
    } finally {
      setNrLoadingIds((prev) => prev.filter((id) => id !== transaction.uid)); // Stop loading
    }
  };

  // Function to handle showing the upload modal
  const handleUploadReceipt = (transaction) => {
    setCurrentTransaction(transaction);
    setSelectedFile(null);
    setShowUploadModal(true);

    // Reset the file input if we have a ref to it
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Function to handle CSV file selection
  const handleCsvFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (limit to 8MB, same as receipt uploads)
      if (selectedFile.size > 8 * 1024 * 1024) {
        setUploadError(
          "File size exceeds 8MB limit. Please upload a smaller file."
        );
        setFile(null);
        return;
      }

      // Rest of the existing validation...
      const validExtensions = [
        ".csv",
        ".ofx",
        ".qfx",
        ".mt940",
        ".pdf",
        ".jpg",
        ".jpeg",
        ".png",
        ".gif",
      ];
      const fileExt = "." + selectedFile.name.split(".").pop().toLowerCase();

      if (!validExtensions.includes(fileExt)) {
        setUploadError(
          "Please upload a supported file format (.csv, .ofx, .qfx, .mt940, .pdf, .jpg, .jpeg, .png, .gif)"
        );
        setFile(null);
        return;
      }

      setFile(selectedFile);
      setUploadError(null);
    }
  };

  // Function to handle receipt file selection
  const handleReceiptFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Check file size (limit to 8MB)
      if (file.size > 8 * 1024 * 1024) {
        toast.error("File size exceeds 8MB limit");
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      setSelectedFile(file);
    }
  };

  // Function to handle the upload submission
  const handleUploadSubmit = async () => {
    if (!selectedFile || !currentTransaction) {
      toast.error("Please select a receipt file first");
      return;
    }

    try {
      setUploadLoading(true);

      const formData = new FormData();
      formData.append("receipt", selectedFile);

      // Add tag and category data
      formData.append("tag", selectedTag);

      // Add transaction details to the form data
      Object.keys(currentTransaction).forEach((key) => {
        if (typeof currentTransaction[key] !== "object") {
          // Skip objects, they can't be serialized properly
          formData.append(key, currentTransaction[key]);
        }
      });

      const response = await axios.post(
        "/api/upload-receipt-for-transaction",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // In the handleUploadSubmit function, after successful upload
      if (response.data.success) {
        // Create a unique identifier for the current transaction
        const transactionIdentifier = {
          id: currentTransaction.id,
          date: currentTransaction.date,
          amount: currentTransaction.amount,
        };

        console.log("Transaction to remove:", transactionIdentifier);

        // Update the processedTransactions state
        setProcessedTransactions((prev) => ({
          ...prev,
          [currentTransaction.uid]: {
            status: "uploaded",
            receipt: response.data.receipt,
          },
        }));

        // Create a copy of the current missingReceipts array for debugging
        const receiptsBeforeRemoval = [...missingReceipts];
        console.log("Receipts before removal:", receiptsBeforeRemoval.length);

        // Remove only the processed transaction from missingReceipts
        // Using a more precise comparison
        setMissingReceipts((prev) => {
          const filtered = prev.filter(
            (item) => item.uid !== currentTransaction.uid
          );
          console.log("Filtered receipts count:", filtered.length);
          return filtered;
        });
        // Add to matches
        // Add to matches (always including the transaction)
        setMatches((prev) => [
          ...prev,
          {
            transaction: currentTransaction,
            receipt: response.data.receipt,
            // No need for alreadySubmitted flag here since this is a new upload
          },
        ]);

        toast.success("Receipt uploaded and expense added successfully");
        setShowUploadModal(false);
      } else {
        toast.error(response.data.message || "Failed to upload receipt");
      }
    } catch (error) {
      console.error("Error uploading receipt:", error);
      toast.error(
        error.response?.data?.message ||
          "An error occurred while uploading the receipt"
      );
    } finally {
      setUploadLoading(false);
    }
  };

  // Render the Upload step
  // Add this to your existing state variables at the top of the component
  const [isDragging, setIsDragging] = useState(false);

  // Add these handler functions inside the component but outside any render functions
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];

      // Check for supported file types
      const validExtensions = [".csv", ".ofx", ".qfx", ".mt940"];
      const fileExt = "." + droppedFile.name.split(".").pop().toLowerCase();

      if (validExtensions.includes(fileExt)) {
        // Set the file and clear any previous errors
        setFile(droppedFile);
        setUploadError(null);

        // Optionally auto-process the file
        // setTimeout(() => handleCsvUpload(), 500);
      } else {
        setUploadError(
          "Please upload a supported bank statement file format (.csv, .ofx, .qfx, .mt940)"
        );
      }
    }
  };

  // Then replace your renderUploadStep function with this version
  const renderUploadStep = () => (
    <div className="reconciliation-upload-container">
      <div className="upload-card">
        <h3>Upload Credit Card Statement</h3>
        {/* File Drop Zone */}
        <div
          className={`file-drop-zone ${isDragging ? "active" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById("csv-upload").click()}
        >
          <div className="drop-icon">
            <i className="fas fa-cloud-upload-alt"></i>
          </div>
          <div className="drop-text">Drag & Drop Your Bank Statement Here</div>
          <div className="drop-subtext">or click to browse files</div>
        </div>
        <div className="file-upload-area">
          <input
            type="file"
            id="csv-upload"
            accept=".csv,.ofx,.qfx,.mt940,.pdf,.jpg,.jpeg,.png,.gif"
            onChange={handleCsvFileChange}
            style={{ display: "none" }}
          />

          {!file && (
            <label htmlFor="csv-upload" className="file-upload-button">
              <i className="fas fa-file-alt"></i>
              Select Bank Statement
            </label>
          )}

          {file && (
            <div className="selected-file">
              <div>
                <i className="fas fa-file-csv"></i>
                {file.name}
              </div>
              <div className="file-size">
                {(file.size / 1024).toFixed(1)} KB
              </div>
            </div>
          )}
        </div>
        {file && (
          <button
            className="process-button"
            disabled={isUploading}
            onClick={handleCsvUpload}
          >
            {isUploading ? (
              <>
                <TailSpin color="#ffffff" height={20} width={20} />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <i className="fas fa-sync-alt"></i>
                <span>Process Statement</span>
              </>
            )}
          </button>
        )}
        {/* Processing animation when uploading */}
        {isUploading && (
          <div className="processing-animation">
            <div className="spinner-container">
              <TailSpin color="#0066ff" height={40} width={40} />
            </div>
            <div className="processing-text">Processing Your Statement</div>
            <div className="processing-subtext">
              This may take a few moments...
            </div>
            {batchProcessing && (
              <div className="upload-progress">
                <div
                  className="upload-progress-bar"
                  style={{
                    width: `${batchProgress}%`,
                    animation: "progress-bar-stripes 1s linear infinite",
                    background:
                      "linear-gradient(45deg, rgba(0,102,255,0.5) 25%, rgba(0,102,255,0.8) 25%, rgba(0,102,255,0.8) 50%, rgba(0,102,255,0.5) 50%, rgba(0,102,255,0.5) 75%, rgba(0,102,255,0.8) 75%, rgba(0,102,255,0.8))",
                    backgroundSize: "20px 20px",
                    transition: "width 0.5s ease-in-out",
                  }}
                ></div>
              </div>
            )}
            <div className="progress-percentage">
              {batchProcessing ? `${batchProgress}%` : ""}
            </div>
          </div>
        )}
      </div>

      {uploadError && (
        <div className="upload-error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{uploadError}</span>
        </div>
      )}

      <div className="supported-formats">
        <h4>Supported File Formats</h4>
        <ul>
          <li>
            <i className="fas fa-file-csv"></i>
            <span>Bank Statement CSV</span>
          </li>
          <li>
            <i className="fas fa-file-alt"></i>
            <span>OFX / QFX Files</span>
          </li>
          <li>
            <i className="fas fa-file-invoice"></i>
            <span>MT-940 Format</span>
          </li>
          {/* Add this new item */}
          <li>
            <i className="fas fa-file-pdf"></i>
            <span>Bank Statement PDF</span>
          </li>
          <li>
            <i className="fas fa-magic"></i>
            <span>Auto-Detection</span>
          </li>
          <li>
            <i className="fas fa-camera"></i>
            <span>Statement Photos</span>
          </li>
        </ul>
      </div>
    </div>
  );

  // Render the Review step
  const renderReviewStep = () => (
    <div className="reconciliation-review-container">
      <div className="review-header">
        <h2>Reconcile</h2>
      </div>

      {/* Tab navigation with stats included */}
      <div className="reconciliation-tabs">
        <button
          className={`reconciliation-tab ${
            activeReviewTab === "missing" ? "active" : ""
          }`}
          onClick={() => setActiveReviewTab("missing")}
        >
          Missing Receipts ({missingReceipts.length})
          {ignoredTransactions.length > 0 &&
            ` (${ignoredTransactions.length} ignored)`}
        </button>
        <button
          className={`reconciliation-tab ${
            activeReviewTab === "matched" ? "active" : ""
          }`}
          onClick={() => setActiveReviewTab("matched")}
        >
          Matched ({matches.length} of {ccTransactions.length})
        </button>
        {exchangeRateUpdates.length > 0 && (
          <button
            className={`reconciliation-tab ${
              activeReviewTab === "rates" ? "active" : ""
            }`}
            onClick={() => setActiveReviewTab("rates")}
          >
            Exchange Rates ({exchangeRateUpdates.length})
          </button>
        )}
      </div>

      {/* Show content based on active tab */}
      {activeReviewTab === "missing" && (
        <>
          {missingReceipts.length > 0 ? (
            <div className="reconciliation-section">
              <h3>Missing Receipts</h3>
              <p className="section-description">
                These credit card transactions don't have matching receipts in
                your records.
              </p>

              {/* Batch actions */}
              <div className="batch-actions">
                <button
                  className="batch-action-button"
                  onClick={async () => {
                    // Filter selected transactions
                    const selectedTransactions = missingReceipts.filter(
                      (t) => t.selected
                    );
                    if (selectedTransactions.length === 0) {
                      toast.warning("Please select at least one transaction");
                      return;
                    }

                    // Activate batch processing mode
                    setBatchProcessing(true);
                    setBatchProgress(0);

                    let processedCount = 0;
                    let errorCount = 0;
                    const totalCount = selectedTransactions.length;

                    // Process in chunks of 10
                    const CHUNK_SIZE = 10;

                    try {
                      // Process all chunks
                      for (let i = 0; i < totalCount; i += CHUNK_SIZE) {
                        // Get current chunk
                        const chunk = selectedTransactions.slice(
                          i,
                          Math.min(i + CHUNK_SIZE, totalCount)
                        );

                        // Update progress to show which chunk we're processing
                        setBatchProgress(Math.round((i / totalCount) * 100));

                        // Process transactions in the chunk SEQUENTIALLY, not in parallel
                        for (let j = 0; j < chunk.length; j++) {
                          const transaction = chunk[j];
                          try {
                            // Wait for each transaction to complete before starting the next one
                            await handleMarkNoReceiptRequired(transaction);
                            processedCount++;

                            // Update progress after each transaction
                            const completedCount = i + j + 1;
                            setBatchProgress(
                              Math.round((completedCount / totalCount) * 100)
                            );

                            // Add a small delay between transactions within the chunk
                            if (j < chunk.length - 1) {
                              await new Promise((resolve) =>
                                setTimeout(resolve, 300)
                              );
                            }
                          } catch (error) {
                            console.error(
                              "Error marking no receipt required:",
                              error
                            );
                            errorCount++;
                          }
                        }

                        // Longer delay between chunks to let the server breathe
                        if (i + CHUNK_SIZE < totalCount) {
                          // Longer delay between chunks (1 second)
                          await new Promise((resolve) =>
                            setTimeout(resolve, 1000)
                          );
                        }
                      }

                      // Set final progress
                      setBatchProgress(100);

                      // Show final results
                      if (errorCount === 0) {
                        toast.success(
                          `Marked ${processedCount} transactions as 'No Receipt Required'`
                        );
                      } else if (processedCount > 0) {
                        toast.warning(
                          `Marked ${processedCount} transactions, but ${errorCount} failed`
                        );
                      } else {
                        toast.error("Failed to mark transactions");
                      }
                    } catch (error) {
                      console.error("Batch processing error:", error);
                      toast.error("An error occurred during batch processing");
                    } finally {
                      // Reset batch processing state after a brief delay to show 100%
                      setTimeout(() => {
                        setBatchProcessing(false);
                        setBatchProgress(0);
                      }, 500);
                    }
                  }}
                  disabled={batchProcessing}
                >
                  {batchProcessing ? (
                    <>
                      <TailSpin color="#ffffff" height={16} width={16} />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check-circle"></i>
                      Mark Selected as No Receipt Required
                    </>
                  )}
                </button>
                {/* Show progress bar during batch processing */}
                {batchProcessing && (
                  <div className="batch-processing-progress">
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${batchProgress}%` }}
                      ></div>
                    </div>
                    <div className="progress-percentage">{batchProgress}%</div>
                  </div>
                )}
                <div className="selection-actions">
                  <button
                    className="selection-button"
                    onClick={() => {
                      // Select all transactions
                      setMissingReceipts((prev) =>
                        prev.map((t) => ({ ...t, selected: true }))
                      );
                    }}
                  >
                    Select All
                  </button>

                  <button
                    className="selection-button"
                    onClick={() => {
                      // Deselect all transactions
                      setMissingReceipts((prev) =>
                        prev.map((t) => ({ ...t, selected: false }))
                      );
                    }}
                  >
                    Deselect All
                  </button>
                </div>
              </div>

              <div className="missing-receipts-list">
                {missingReceipts.map((transaction) => (
                  <div
                    key={transaction.uid}
                    className={`missing-receipt-card ${
                      transaction.selected ? "selected" : ""
                    } ${transaction.bankType || ""} ${
                      transaction.isForeignTransaction ? "foreign" : ""
                    }`}
                  >
                    <div className="transaction-selection">
                      <input
                        type="checkbox"
                        checked={transaction.selected || false}
                        onChange={(e) => {
                          // Prevent the event from bubbling up
                          e.stopPropagation();

                          setMissingReceipts((prev) =>
                            prev.map((t) =>
                              t.uid === transaction.uid
                                ? { ...t, selected: !t.selected }
                                : t
                            )
                          );
                        }}
                      />
                    </div>

                    <div className="transaction-details">
                      <div className="transaction-date">
                        {(() => {
                          const dateObj = new Date(transaction.date);
                          return isNaN(dateObj.getTime())
                            ? "—"
                            : dateObj.toLocaleDateString();
                        })()}
                      </div>
                      <div className="transaction-merchant">
                        {(() => {
                          // First try to use the merchant field if it's clean
                          if (
                            transaction.merchant &&
                            !transaction.merchant.includes("AMT") &&
                            !/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2}/.test(
                              transaction.merchant
                            )
                          ) {
                            return transaction.merchant;
                          }

                          // Otherwise, clean up the description field
                          const desc = transaction.description || "";
                          // Check if desc starts with a date-like pattern (Mar 6, etc.)
                          const dateRegex =
                            /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2}/;
                          const dateMatch = desc.match(dateRegex);

                          // Remove date prefix if present
                          let cleanDesc = desc;
                          if (dateMatch) {
                            cleanDesc = cleanDesc
                              .substring(dateMatch[0].length)
                              .trim();
                          }

                          // Remove city/location suffix if present
                          const locationRegex =
                            /\s+(FORT LAUDERD|WILTON MANOR|FT LAUDERDALE)[A-Z]*$/;
                          const locationMatch = cleanDesc.match(locationRegex);
                          if (locationMatch) {
                            cleanDesc = cleanDesc
                              .substring(0, locationMatch.index)
                              .trim();
                          }

                          // Remove "AMT X.XX USD" foreign amount text
                          const amtRegex = /\s*AMT\s+[\d.]+\s+[A-Z]{3}\s*/g;
                          cleanDesc = cleanDesc.replace(amtRegex, " ").trim();

                          return cleanDesc;
                        })()}
                      </div>
                      {transaction.location && (
                        <div className="transaction-location">
                          {(() => {
                            const desc =
                              transaction.merchant ||
                              transaction.description ||
                              "";
                            const locationRegex =
                              /(FORT LAUDERD|WILTON MANOR|FT LAUDERDALE)[A-Z]*/;
                            const locationMatch = desc.match(locationRegex);
                            return locationMatch
                              ? locationMatch[0]
                              : transaction.location || "";
                          })()}
                        </div>
                      )}

                      <div className="transaction-amount">
                        {transaction.isForeignTransaction &&
                        transaction.originalAmount &&
                        transaction.originalCurrency ? (
                          <span>
                            {/* Handle the case where amount and originalAmount are the same due to parsing error */}
                            {transaction.needsAmountFixInUI ? (
                              // This is a special case where CAD amount wasn't parsed correctly
                              <>
                                {transaction.originalAmount.toFixed(2)}{" "}
                                {transaction.originalCurrency}
                                <span className="warning-text">
                                  {" "}
                                  (Amount shown in{" "}
                                  {transaction.originalCurrency})
                                </span>
                              </>
                            ) : (
                              // Normal case - show CAD with original amount in parentheses
                              <>
                                {transaction.amount.toFixed(2)}{" "}
                                {transaction.currency}
                                <span className="foreign-amount">
                                  {" "}
                                  (Original:{" "}
                                  {transaction.originalAmount.toFixed(2)}{" "}
                                  {transaction.originalCurrency})
                                </span>
                              </>
                            )}
                          </span>
                        ) : (
                          <span>
                            {transaction.amount.toFixed(2)}{" "}
                            {transaction.currency}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Only show actions if not already processed */}
                    {!processedTransactions[transaction.uid] ? (
                      <div className="missing-receipt-actions">
                        <button
                          className="action-button3 upload-button2"
                          onClick={() => handleUploadReceipt(transaction)}
                        >
                          <i className="fas fa-upload"></i>
                          Upload Receipt
                        </button>
                        <button
                          className="action-button3 skip-button"
                          onClick={() => {
                            // Add toast here after successful operation
                            handleMarkNoReceiptRequired(transaction).then(
                              (success) => {
                                if (success) {
                                  toast.success(
                                    'Transaction added without a receipt."'
                                  );
                                }
                              }
                            );
                          }}
                          disabled={nrLoadingIds.includes(transaction.uid)}
                        >
                          {nrLoadingIds.includes(transaction.uid) ? (
                            <TailSpin color="#ffffff" height={16} width={16} />
                          ) : (
                            <>
                              <i className="fas fa-check-circle"></i>
                              No Receipt Required
                            </>
                          )}
                        </button>
                        <button
                          className="action-button3 ignore-button"
                          onClick={() => handleIgnoreTransaction(transaction)}
                        >
                          <i className="fas fa-ban"></i>
                          Ignore
                        </button>
                      </div>
                    ) : (
                      <div className="processed-status">
                        {processedTransactions[transaction.uid].status ===
                        "uploaded" ? (
                          <span className="status-uploaded">
                            <i className="fas fa-upload"></i> Receipt Uploaded
                          </span>
                        ) : (
                          <span className="status-no-receipt">
                            <i className="fas fa-check-circle"></i> No Receipt
                            Required
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <p className="empty-message">
                All transactions have been processed! No missing receipts
                remain.
              </p>
              {matches.length > 0 && (
                <button
                  className="tab-button"
                  onClick={() => setActiveReviewTab("matched")}
                >
                  View Matched Transactions
                </button>
              )}
            </div>
          )}
        </>
      )}

      {activeReviewTab === "matched" && (
        <>
          {matches.length > 0 ? (
            <div className="reconciliation-section">
              <h3>
                Matched Transactions ({matches.length} of{" "}
                {ccTransactions.length})
              </h3>
              <p className="section-description">
                These transactions match receipts in your records.
              </p>

              {/* Exchange Rate Updates Summary - show at top if any exist */}
              {exchangeRateUpdates.length > 0 && (
                <div className="exchange-rate-summary">
                  <h4>Exchange Rate Updates Needed</h4>
                  <p>
                    There are {exchangeRateUpdates.length} transactions with
                    exchange rate differences that need to be updated.
                  </p>
                  <button
                    className="update-all-rates-button"
                    onClick={handleUpdateAllExchangeRates}
                    disabled={isUpdatingAllRates}
                  >
                    {isUpdatingAllRates ? (
                      <>
                        <TailSpin color="#ffffff" height={16} width={16} />
                        <span>Updating Rates...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sync-alt"></i>
                        <span>Update All Exchange Rates</span>
                      </>
                    )}
                  </button>

                  <div className="exchange-rate-updates-list">
                    {exchangeRateUpdates.map((update, index) => (
                      <div
                        key={`exchange-${index}`}
                        className={`exchange-rate-card ${
                          update.impact > 1 ? "significant-impact" : ""
                        }`}
                      >
                        {" "}
                        <div className="update-details">
                          <span className="merchant">
                            {update.match.receipt.merchant}
                          </span>
                          <span className="amount">
                            {update.match.receipt.amount.toFixed(2)}{" "}
                            {update.match.receipt.currency}
                          </span>
                          <span className="rates">
                            {update.oldRate.toFixed(4)} →{" "}
                            {update.newRate.toFixed(4)}
                          </span>
                          <span
                            className={`impact ${
                              update.impact > 1 ? "significant" : ""
                            }`}
                          >
                            Impact: {update.impact.toFixed(2)} {homeCurrency}
                          </span>
                        </div>
                        <button
                          className="update-button"
                          onClick={() =>
                            handleUpdateExchangeRate({
                              rowIndex: update.match.receipt.rowIndex,
                              exchangeRate: update.newRate,
                              functionalAmount:
                                update.newFunctionalAmount ||
                                update.match.receipt.amount / update.newRate,
                            })
                          }
                          disabled={updatingRateIds.includes(
                            update.match.receipt.rowIndex
                          )}
                        >
                          {updatingRateIds.includes(
                            update.match.receipt.rowIndex
                          ) ? (
                            <TailSpin color="#ffffff" height={16} width={16} />
                          ) : (
                            "Update"
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Simplified Matched Transactions List */}
              {/* Add info message about already submitted matches */}
              {matches.some((match) => match.alreadySubmitted) && (
                <div className="info-message">
                  <i className="fas fa-info-circle"></i>
                  {
                    matches.filter((match) => match.alreadySubmitted).length
                  }{" "}
                  transaction(s) match that are already submitted in an expense
                  report.
                </div>
              )}

              <div className="matches-list-condensed">
                {matches
                  .filter(
                    (m) =>
                      m.transaction && // has a transaction object
                      m.transaction.currency && // currency is defined
                      m.transaction.date && // date is defined
                      m.transaction.amount != null // amount is defined (≠ undefined/null)
                  )
                  .map((match) => (
                    /* Existing card JSX */
                    <div
                      key={match.transaction.uid || `match-${Math.random()}`}
                      className={`match-card-condensed ${
                        match.alreadySubmitted ? "already-submitted" : ""
                      }`}
                    >
                      <div className="match-date">
                        {new Date(match.transaction.date).toLocaleDateString()}
                      </div>
                      <div className="match-merchant">
                        {match.transaction.description}
                      </div>
                      <div className="match-amount">
                        {match.transaction.amount.toFixed(2)}{" "}
                        {match.transaction.currency}
                      </div>

                      {/* Add this badge for already submitted matches */}
                      {match.alreadySubmitted && (
                        <div className="already-submitted-badge">
                          <i className="fas fa-check-circle"></i> Already
                          Submitted
                        </div>
                      )}

                      {/* Show exchange rate difference notification if needed */}
                      {/* Only show for non-submitted matches */}
                      {!match.alreadySubmitted &&
                        (() => {
                          // Check if this is actually a foreign transaction (in a non-home currency)
                          const isForeignTransaction =
                            match.transaction.currency !== homeCurrency ||
                            match.receipt.currency !== homeCurrency;

                          // Check if the exchange rates actually differ
                          const ratesDiffer =
                            Math.abs(
                              match.receipt.exchangeRate -
                                match.transaction.exchangeRate
                            ) > 0.0001;

                          // Only show the exchange rate UI for foreign transactions with different rates
                          return isForeignTransaction && ratesDiffer ? (
                            <div className="exchange-rate-update">
                              {/* Content of the exchange rate update UI - keep this part unchanged */}
                              <div className="exchange-rate-info">
                                <span>Exchange Rate: </span>
                                <span className="current-rate">
                                  {match.receipt.exchangeRate.toFixed(4)}
                                </span>
                                <span className="arrow">→</span>
                                <span className="new-rate">
                                  {match.transaction.exchangeRate.toFixed(4)}
                                </span>

                                {/* Calculate functional amount display */}
                                {(() => {
                                  // Use the improved functional amount calculations here
                                  const receiptFunctional =
                                    match.receipt.functionalTotal &&
                                    match.receipt.functionalTotal > 0
                                      ? match.receipt.functionalTotal
                                      : match.receipt.amount /
                                        match.receipt.exchangeRate;
                                  const transactionFunctional =
                                    match.transaction.functionalAmount &&
                                    match.transaction.functionalAmount > 0
                                      ? match.transaction.functionalAmount
                                      : match.transaction.amount /
                                        match.transaction.exchangeRate;

                                  // Calculate impact
                                  const impact = Math.abs(
                                    receiptFunctional - transactionFunctional
                                  );

                                  return (
                                    <>
                                      <div className="functional-amounts">
                                        <span>{homeCurrency} Amount: </span>
                                        <span className="receipt-amount">
                                          {receiptFunctional.toFixed(2)}
                                        </span>
                                        <span className="arrow">→</span>
                                        <span className="bank-amount">
                                          {transactionFunctional.toFixed(2)}
                                        </span>
                                      </div>
                                      <span
                                        className={`impact-amount ${
                                          impact > 1 ? "significant" : ""
                                        }`}
                                      >
                                        (Difference: {impact.toFixed(2)}{" "}
                                        {homeCurrency})
                                      </span>
                                    </>
                                  );
                                })()}
                              </div>
                              <button
                                className="update-rate-button"
                                onClick={() =>
                                  handleUpdateExchangeRate({
                                    rowIndex: match.receipt.rowIndex,
                                    exchangeRate:
                                      match.transaction.exchangeRate,
                                    functionalAmount:
                                      match.transaction.functionalAmount ||
                                      match.transaction.amount /
                                        match.transaction.exchangeRate,
                                  })
                                }
                                disabled={updatingRateIds.includes(
                                  match.receipt.rowIndex
                                )}
                              >
                                {updatingRateIds.includes(
                                  match.receipt.rowIndex
                                ) ? (
                                  <>
                                    <TailSpin
                                      color="#ffffff"
                                      height={16}
                                      width={16}
                                    />
                                    <span>Updating...</span>
                                  </>
                                ) : (
                                  "Update Exchange Rate"
                                )}
                              </button>
                            </div>
                          ) : null;
                        })()}
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <p className="empty-message">No matched transactions found.</p>
              {missingReceipts.length > 0 && (
                <button
                  className="tab-button"
                  onClick={() => setActiveReviewTab("missing")}
                >
                  Return to Missing Receipts
                </button>
              )}
            </div>
          )}
        </>
      )}

      {activeReviewTab === "rates" && (
        <>
          {(() => {
            // Filter out non-foreign transactions and transactions with same rates
            const validExchangeRateUpdates = exchangeRateUpdates.filter(
              (update) => {
                // Use the homeCurrency prop instead of hardcoded "CAD"
                const isForeignTransaction =
                  update.match.transaction.currency !== homeCurrency ||
                  update.match.receipt.currency !== homeCurrency;

                // Check if exchange rates actually differ
                const ratesDiffer =
                  Math.abs(update.oldRate - update.newRate) > 0.0001;

                // Only keep genuine foreign transactions with different rates
                return isForeignTransaction && ratesDiffer;
              }
            );

            return validExchangeRateUpdates.length > 0 ? (
              <div className="reconciliation-section">
                <h3>Exchange Rate Updates</h3>
                <p className="section-description">
                  Apply the actual exchange rates from your credit card
                  statement to your expense records for accurate accounting.
                </p>

                <div className="section-actions">
                  <button
                    className="update-all-button"
                    onClick={() => {
                      // Only update valid exchange rates
                      const updatesToProcess = validExchangeRateUpdates.map(
                        (update) => ({
                          rowIndex: update.match.receipt.rowIndex,
                          exchangeRate: update.newRate,
                          functionalAmount:
                            update.newFunctionalAmount > 0
                              ? update.newFunctionalAmount
                              : update.match.transaction.amount /
                                update.newRate,
                        })
                      );

                      handleUpdateAllExchangeRates(updatesToProcess);
                    }}
                    disabled={isUpdatingAllRates}
                  >
                    {isUpdatingAllRates ? (
                      <>
                        <TailSpin color="#ffffff" height={16} width={16} />
                        <span>Updating Rates...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sync-alt"></i>
                        <span>Update All Exchange Rates</span>
                      </>
                    )}
                  </button>
                </div>

                <div className="exchange-rates-list">
                  {validExchangeRateUpdates.map((update, index) => {
                    // Ensure functional amounts are valid numbers (never zero)
                    const oldFunctionalAmount =
                      update.oldFunctionalAmount &&
                      update.oldFunctionalAmount > 0
                        ? update.oldFunctionalAmount
                        : update.match.receipt.amount / update.oldRate;

                    const newFunctionalAmount =
                      update.newFunctionalAmount &&
                      update.newFunctionalAmount > 0
                        ? update.newFunctionalAmount
                        : update.match.transaction.amount / update.newRate;

                    // Calculate actual impact with valid amounts
                    const actualImpact = Math.abs(
                      oldFunctionalAmount - newFunctionalAmount
                    );

                    return (
                      <div
                        key={`exchange-card-${
                          update.match.receipt.rowIndex || index
                        }-${index}`}
                        className={`exchange-rate-card ${
                          actualImpact > 1 ? "significant-impact" : ""
                        }`}
                      >
                        <div className="exchange-rate-details">
                          <div className="exchange-rate-merchant">
                            {update.match.receipt.merchant}
                          </div>
                          <div className="exchange-rate-date">
                            {new Date(
                              update.match.receipt.date
                            ).toLocaleDateString()}
                          </div>
                          <div className="exchange-rate-amount">
                            {update.match.receipt.amount.toFixed(2)}{" "}
                            {update.match.receipt.currency}
                          </div>
                        </div>

                        <div className="exchange-rate-change">
                          <div className="rate-section">
                            <div className="rate-old">
                              <span className="rate-label">Current Rate:</span>
                              <span className="rate-value">
                                {update.oldRate.toFixed(4)}
                              </span>
                            </div>
                            <div className="rate-arrow">→</div>
                            <div className="rate-new">
                              <span className="rate-label">Bank Rate:</span>
                              <span className="rate-value">
                                {update.newRate.toFixed(4)}
                              </span>
                            </div>
                          </div>

                          <div className="functional-amount-section">
                            <div className="func-old">
                              <span className="func-label">
                                Current Amount ({homeCurrency}):
                              </span>
                              <span className="func-value crossed-out">
                                {oldFunctionalAmount.toFixed(2)}
                              </span>
                            </div>
                            <div className="func-arrow">→</div>
                            <div className="func-new">
                              <span className="func-label">
                                Actual Amount ({homeCurrency}):
                              </span>
                              <span className="func-value">
                                {newFunctionalAmount.toFixed(2)}
                              </span>
                            </div>
                          </div>

                          <div className="rate-impact">
                            <span className="impact-label">Difference:</span>
                            <span
                              className={`impact-value ${
                                actualImpact > 1 ? "significant" : ""
                              }`}
                            >
                              {actualImpact.toFixed(2)} {homeCurrency}
                            </span>
                          </div>
                        </div>

                        <div className="exchange-rate-actions">
                          <button
                            className="update-single-rate-button"
                            onClick={() =>
                              handleUpdateExchangeRate({
                                rowIndex: update.match.receipt.rowIndex,
                                exchangeRate: update.newRate,
                                functionalAmount: newFunctionalAmount,
                              })
                            }
                            disabled={updatingRateIds.includes(
                              update.match.receipt.rowIndex
                            )}
                          >
                            {updatingRateIds.includes(
                              update.match.receipt.rowIndex
                            ) ? (
                              <TailSpin
                                color="#ffffff"
                                height={16}
                                width={16}
                              />
                            ) : (
                              "Update This Rate"
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <p className="empty-message">
                  No exchange rate updates available.
                </p>
                <button
                  className="tab-button"
                  onClick={() => setActiveReviewTab("missing")}
                >
                  Return to Missing Receipts
                </button>
              </div>
            );
          })()}
        </>
      )}

      <div className="reconciliation-complete-actions">
        <button
          className="complete-button"
          onClick={async () => {
            setIsCompleting(true);
            try {
              // Limited timeout for better UX
              const cleanupPromise = cleanupAllJobs();
              const timeoutPromise = new Promise((resolve) =>
                setTimeout(resolve, 1500)
              );

              // Wait for cleanup or timeout, whichever comes first
              await Promise.race([cleanupPromise, timeoutPromise]);

              // Continue cleaning up in background if not done
              if (!isCleaningUp) {
                onComplete && onComplete();
              } else {
                // If still cleaning, finish it in background after navigating
                onComplete && onComplete();
                cleanupPromise.catch((err) =>
                  console.error("Background cleanup error:", err)
                );
              }
            } catch (err) {
              console.error("Error during completion:", err);
              onComplete && onComplete(); // Still navigate even if there was an error
            }
          }}
          disabled={isCompleting}
        >
          {isCompleting ? (
            <>
              <TailSpin color="#ffffff" height={16} width={16} />
              <span>Completing...</span>
            </>
          ) : (
            "Complete Reconciliation"
          )}
        </button>
      </div>
    </div>
  );

  // Render the upload modal
  const renderUploadModal = () => {
    if (!showUploadModal || !currentTransaction) return null;

    return (
      <div
        className="receipt-upload-modal-overlay"
        onClick={() => !uploadLoading && setShowUploadModal(false)}
      >
        <div
          className="receipt-upload-modal"
          onClick={(e) => {
            e.stopPropagation(); // Prevent event bubbling
          }}
          onWheel={(e) => {
            // Don't prevent default wheel behavior so scrolling works
            e.stopPropagation();
          }}
          onTouchMove={(e) => {
            // Don't prevent default touch behavior so scrolling works on mobile
            e.stopPropagation();
          }}
        >
          <div className="modal-header">
            <h3>Upload Receipt</h3>
            {!uploadLoading && (
              // Find the close button in your reconciliation modal
              <button
                className="close-button"
                onClick={() => setShowUploadModal(false)}
                aria-label="Close"
              >
                &times;
              </button>
            )}
          </div>

          <div className="modal-scrollable-content">
            <div className="transaction-details">
              <div className="detail-row">
                <span className="detail-label">Date:</span>
                <span className="detail-value">
                  {new Date(currentTransaction.date).toLocaleDateString()}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Merchant:</span>
                <span className="detail-value">
                  {currentTransaction.merchant ||
                    currentTransaction.description}
                </span>
              </div>
              {currentTransaction.location && (
                <div className="detail-row">
                  <span className="detail-label">Location:</span>
                  <span className="detail-value">
                    {currentTransaction.location}
                  </span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Amount:</span>
                <span className="detail-value">
                  {currentTransaction.isForeignTransaction ? (
                    <>
                      {currentTransaction.amount.toFixed(2)}{" "}
                      {currentTransaction.currency}
                      <div className="foreign-transaction-detail">
                        Original: {currentTransaction.originalAmount.toFixed(2)}{" "}
                        {currentTransaction.originalCurrency}
                        {currentTransaction.exchangeRate !== 1 && (
                          <span className="exchange-rate">
                            (Rate: {currentTransaction.exchangeRate.toFixed(4)})
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      {currentTransaction.amount.toFixed(2)}{" "}
                      {currentTransaction.currency}
                    </>
                  )}
                </span>
              </div>
            </div>

            <div className="file-upload-area">
              <p className="upload-instructions">
                Select a receipt image or PDF to upload (max 8MB)
              </p>
              <input
                type="file"
                ref={fileInputRef}
                id="receipt-file"
                onChange={handleReceiptFileChange}
                accept="image/jpeg,image/png,image/gif,application/pdf"
                className="file-input"
                disabled={uploadLoading}
              />
              <label htmlFor="receipt-file" className="file-upload-button">
                <i className="fas fa-upload"></i>
                Choose File
              </label>

              {selectedFile && (
                <div className="selected-file-info">
                  <span className="file-name">{selectedFile.name}</span>
                  <span className="file-size">
                    ({(selectedFile.size / 1024).toFixed(0)} KB)
                  </span>
                </div>
              )}
            </div>

            {/* Tag Selection */}
            <div className="tag-selection-area">
              <label>Add Tag</label>
              <TagSelector
                selectedTag={selectedTag}
                onTagSelect={(tag) => setSelectedTag(tag)}
                isCompact={false}
                buttonLabel="Add a Tag"
                className="receipt-tag-selector"
                disabled={uploadLoading}
              />
            </div>

            <div className="modal-footer">
              <button
                className="submit-button"
                onClick={handleUploadSubmit}
                disabled={!selectedFile || uploadLoading}
              >
                {uploadLoading ? (
                  <>
                    <TailSpin color="#ffffff" height={16} width={16} />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    <span>Upload Receipt</span>
                  </>
                )}
              </button>

              <button
                className="cancel-button"
                onClick={() => setShowUploadModal(false)}
                disabled={uploadLoading}
              >
                <i className="fas fa-times"></i>
                <span>Cancel</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="credit-card-reconciliation-container">
      {currentStep === 1 ? renderUploadStep() : renderReviewStep()}
      {renderUploadModal()}
    </div>
  );
};

export default CreditCardReconciliation;
